import React, { useState } from "react";
import styled from "styled-components";
import { apiClient } from "../../App";
import { History } from "history";
import Input from "../../components/Input";
import IconButton from "../../components/IconButton";
import Button from "../../components/Button";
import { FlashControl } from "../../components/FlashMessage";
import json5 from "json5";
import Spinner from "../../components/Spinner";
import { replayStorage } from "../../storage/ReplayStorage";
import uuid from "uuid";
import MessageSequence from "../Recorder/MessageSequence";
import { Route } from "react-router";

interface Props {
  history: History;
}

const DEFAULT_MESSAGE = `{
    reqId: "will-be-generated",
    data: {
        // Add data here
    }
}`;

const StyledInteractiveBus = styled.div`
  display: flex;

  .col-1 {
    width: 40vw;
    padding: 1rem 1rem 4rem 1rem;
    overflow-y: auto;
    max-height: 100vh;
    border-right: 2px solid ${props => props.theme.colors.gray2};

    textarea {
      margin: 1rem 0;
      width: 100%;
      height: 25vh;
      min-height: 300px;
      font-size: 0.9rem;
      border: 1px solid ${props => props.theme.colors.gray1};
      padding: 0.5rem 0.25rem;
      font-family: ${props => props.theme.monospaceFont};
    }

    .advanced {
      margin: 0.5rem 0 1rem 0;
      display: flex;
      align-items: center;

      > * {
        margin-right: 0.5rem;
        flex: 1;
      }
    }

    .submit-btn {
      margin: 1.5rem 0;
    }

    .response {
      background: ${props => props.theme.colors.gray2};
      padding: 0.5rem;
      border-radius: 4px;
      overflow-x: scroll;
    }
  }

  .col-2 {
    width: 60vw;
    height: 100vh;
    max-width: 100%;
  }
`;

const InteractiveBus: React.FC<Props> = ({ history }) => {
  const { subjectToReplay, messageToReplay, replayMessageId } = getReplayMessage();

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [maxResponses, setMaxResponses] = useState("");
  const [timeout, setBusTimeout] = useState("");
  const [message, setMessage] = useState(messageToReplay || DEFAULT_MESSAGE);
  const [subject, setSubject] = useState(subjectToReplay);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reqId, setReqId] = useState<string | null>(null);

  const sendMessage = async () => {
    const parsedMessage = parseMessage();

    if (!parsedMessage) {
      FlashControl.showWarning("Invalid JSON");
    }

    setLoading(true);

    try {
      // Start recording for this particular reqId
      const { data: recording } = await apiClient.post("/recording", {
        reqId: parsedMessage.reqId
      });

      // Post message on bus
      const { data } = await apiClient.post("/bus", {
        message: parsedMessage,
        subject,
        timeout: (timeout ? Number(timeout) : 10) * 1000,
        maxResponses: maxResponses ? Number(maxResponses) : 0
      });

      setResponse(data);

      const pushPath = `/interactive-bus/recording/${recording.id}/${parsedMessage.reqId}`;
      history.push(pushPath + (replayMessageId ? "?replay=" + replayMessageId : ""));

      setReqId(parsedMessage.reqId);

      setTimeout(() => {
        // TODO: This should probablybe backends responsibility
        apiClient.post(`/recording/${recording.id}/stop`, {});
      }, 1000);
    } catch (err) {
      console.log(err);
      setResponse(null);
      FlashControl.showWarning("Failed to send message: " + err.msg);
    }

    setLoading(false);
  };

  const parseMessage = () => {
    try {
      const parsedMessage = json5.parse(message);
      parsedMessage.reqId = uuid.v4();
      return parsedMessage;
    } catch (err) {
      return false;
    }
  };

  return (
    <StyledInteractiveBus>
      <div className="col-1">
        <Input placeholder="Enter subject" value={subject} onChange={e => setSubject(e.target.value)} />
        <textarea placeholder="Enter JSON message" value={message} onChange={e => setMessage(e.target.value)} />
        <IconButton icon="settings" size="s" onClick={() => setShowAdvanced(!showAdvanced)} text="Advanced settings" />
        {showAdvanced && (
          <div className="advanced">
            <Input
              placeholder="Max responses"
              inputSize="s"
              type="number"
              value={maxResponses}
              onChange={e => setMaxResponses(e.target.value)}
            />
            <Input
              placeholder="Timeout in sec (default 10)"
              inputSize="s"
              type="number"
              value={timeout}
              onChange={e => setBusTimeout(e.target.value)}
            />
          </div>
        )}
        <Button title="Send message" onClick={sendMessage} className="submit-btn" />

        {loading && <Spinner />}
        {response && (
          <div className="response">
            <pre>
              <code>{json5.stringify(response, { space: 2, quote: '"' })}</code>
            </pre>
          </div>
        )}
      </div>
      <div className="col-2">
        <Route path={`/interactive-bus/recording/:id/:reqId`} component={MessageSequence} key={reqId || "n/a"} />
      </div>
    </StyledInteractiveBus>
  );
};

/**
 * Returns message to replay, if any, from local storage.
 */
const getReplayMessage = () => {
  const search = window.location.search;

  if (search.includes("?replay=")) {
    // Load message from local storage if a replay message was passed in
    const messageId = search.replace("?replay=", "");
    const message = replayStorage.getMessage(messageId);

    if (message) {
      return {
        subjectToReplay: message.subject,
        messageToReplay: json5.stringify(message.msg, null, 2),
        replayMessageId: messageId
      };
    }
  }

  return {
    subjectToReplay: "",
    messageToReplay: null,
    replayMessageId: null
  };
};

export default InteractiveBus;
